
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { useSupabaseAuth } from '@/hooks/auth';
import { startOfDay, endOfDay, subDays, format as dateFnsFormat } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

interface DailyStats {
  date: string;
  total_users: number;
  total_groups: number;
  total_expenses: number;
}

interface AnalyticsSummary {
  totalUsers: number;
  totalGroups: number;
  totalExpenses: number;
  dailyStats: DailyStats[];
  isLoading: boolean;
  error: string | null;
}

interface DateRange {
  startDate: Date;
  endDate: Date;
}

// Bangkok timezone for consistency
const TIMEZONE = 'Asia/Bangkok';

export const useAdminAnalytics = () => {
  const [analytics, setAnalytics] = useState<AnalyticsSummary>({
    totalUsers: 0,
    totalGroups: 0,
    totalExpenses: 0,
    dailyStats: [],
    isLoading: true,
    error: null
  });
  
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    // Default to "today" in Bangkok timezone
    const now = new Date();
    const start = startOfDay(now);
    const end = endOfDay(now);
    return { startDate: start, endDate: end };
  });
  
  const { toast } = useToast();
  const { user } = useSupabaseAuth();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const isFetchingRef = useRef<boolean>(false);
  
  useEffect(() => {
    const checkAdminRole = async () => {
      if (!user) return;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();
      
      if (error) {
        console.error('Error checking admin role:', error);
        return;
      }
      
      setIsAdmin(data.role === 'admin');
    };
    
    checkAdminRole();
  }, [user]);
  
  const fetchAnalytics = useCallback(async () => {
    // Prevent concurrent fetches
    if (isFetchingRef.current || !user || !isAdmin) return;
    
    isFetchingRef.current = true;
    
    const startDateStr = formatInTimeZone(dateRange.startDate, TIMEZONE, 'yyyy-MM-dd');
    const endDateStr = formatInTimeZone(dateRange.endDate, TIMEZONE, 'yyyy-MM-dd');
    
    console.log('Fetching analytics with date range:', {
      startDate: startDateStr,
      endDate: endDateStr
    });
    
    setAnalytics(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      // Fetch total counts for the selected period
      const { data: usersData, error: usersError } = await supabase
        .from('profiles')
        .select('id', { count: 'exact' })
        .gte('created_at', startDateStr)
        .lte('created_at', endDateStr + ' 23:59:59');
      
      if (usersError) throw usersError;
      
      const { data: groupsData, error: groupsError } = await supabase
        .from('groups')
        .select('id', { count: 'exact' })
        .gte('created_at', startDateStr)
        .lte('created_at', endDateStr + ' 23:59:59');
      
      if (groupsError) throw groupsError;
      
      const { data: expensesData, error: expensesError } = await supabase
        .from('expenses')
        .select('id', { count: 'exact' })
        .gte('created_at', startDateStr)
        .lte('created_at', endDateStr + ' 23:59:59');
      
      if (expensesError) throw expensesError;
      
      // Try to use the stored procedure, fall back to manual calculation if not available
      try {
        const { data: dailyStats, error: dailyStatsError } = await supabase.rpc(
          'get_daily_stats',
          { start_date: startDateStr, end_date: endDateStr }
        );
        
        if (dailyStatsError) {
          console.log('Using fallback for daily stats:', dailyStatsError);
          throw dailyStatsError;
        }
        
        setAnalytics({
          totalUsers: usersData?.length || 0,
          totalGroups: groupsData?.length || 0,
          totalExpenses: expensesData?.length || 0,
          dailyStats: dailyStats || [],
          isLoading: false,
          error: null
        });
      } catch (error) {
        // Manual calculation for daily stats
        const { data: usersByDay, error: usersDailyError } = await supabase
          .from('profiles')
          .select('created_at')
          .gte('created_at', startDateStr)
          .lte('created_at', endDateStr + ' 23:59:59');
        
        if (usersDailyError) throw usersDailyError;
        
        const { data: groupsByDay, error: groupsDailyError } = await supabase
          .from('groups')
          .select('created_at')
          .gte('created_at', startDateStr)
          .lte('created_at', endDateStr + ' 23:59:59');
        
        if (groupsDailyError) throw groupsDailyError;
        
        const { data: expensesByDay, error: expensesDailyError } = await supabase
          .from('expenses')
          .select('created_at')
          .gte('created_at', startDateStr)
          .lte('created_at', endDateStr + ' 23:59:59');
        
        if (expensesDailyError) throw expensesDailyError;
        
        // Create a map to store daily stats
        const dailyStatsMap = new Map<string, DailyStats>();
        
        // Generate dates within range
        let currentDate = new Date(dateRange.startDate);
        const endDate = new Date(dateRange.endDate);
        
        // Create entries for each day in the range
        while (currentDate <= endDate) {
          const dateStr = formatInTimeZone(currentDate, TIMEZONE, 'yyyy-MM-dd');
          dailyStatsMap.set(dateStr, {
            date: dateStr,
            total_users: 0,
            total_groups: 0,
            total_expenses: 0
          });
          
          // Move to next day
          currentDate = new Date(currentDate);
          currentDate.setDate(currentDate.getDate() + 1);
        }
        
        // Count users by day
        usersByDay?.forEach(user => {
          const date = formatInTimeZone(new Date(user.created_at), TIMEZONE, 'yyyy-MM-dd');
          const stats = dailyStatsMap.get(date);
          if (stats) {
            stats.total_users += 1;
          }
        });
        
        // Count groups by day
        groupsByDay?.forEach(group => {
          const date = formatInTimeZone(new Date(group.created_at), TIMEZONE, 'yyyy-MM-dd');
          const stats = dailyStatsMap.get(date);
          if (stats) {
            stats.total_groups += 1;
          }
        });
        
        // Count expenses by day
        expensesByDay?.forEach(expense => {
          const date = formatInTimeZone(new Date(expense.created_at), TIMEZONE, 'yyyy-MM-dd');
          const stats = dailyStatsMap.get(date);
          if (stats) {
            stats.total_expenses += 1;
          }
        });
        
        // Convert map to array and sort by date
        const calculatedDailyStats = Array.from(dailyStatsMap.values())
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        
        setAnalytics({
          totalUsers: usersData?.length || 0,
          totalGroups: groupsData?.length || 0,
          totalExpenses: expensesData?.length || 0,
          dailyStats: calculatedDailyStats,
          isLoading: false,
          error: null
        });
      }
    } catch (error: any) {
      console.error('Error fetching analytics:', error);
      setAnalytics(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Failed to fetch analytics data'
      }));
      
      toast({
        title: 'Error',
        description: 'Failed to fetch analytics data. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsInitialLoad(false);
      isFetchingRef.current = false;
    }
  }, [dateRange, isAdmin, user, toast]);
  
  // Only fetch when date range or admin status changes
  useEffect(() => {
    if (isAdmin && !isFetchingRef.current) {
      fetchAnalytics();
    }
  }, [isAdmin, fetchAnalytics]);
  
  // Function to update the date range
  const updateDateRange = useCallback((newRange: DateRange) => {
    setDateRange(newRange);
    // Schedule data refresh
    setTimeout(fetchAnalytics, 100);
  }, [fetchAnalytics]);
  
  return {
    analytics,
    dateRange,
    setDateRange: updateDateRange,
    refreshAnalytics: fetchAnalytics,
    isAdmin,
    isInitialLoad
  };
};
